import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { inject, observer } from "mobx-react";
import { Button, Col, Container, Row, Stack } from "react-bootstrap";
import AccountListBox from "../components/AccountListBox/AccountListBox";
import Avatar from "../components/Avatar/Avatar";
import AccountLocaleCard from "../components/AccountLocaleCard/AccountLocaleCard";
import AccountInfoCard from "../components/AccountInfoCard/AccountInfoCard";
import AccountDataAccessCard from "../components/AccountDataAccessCard/AccountDataAccessCard";
import AccountCreateModal from "../components/AccountCreateModal/AccountCreateModal";
import { isEqual } from "lodash";
import BlockKitStore from "../stores/BlockKitStore";
import { AdminUserModel, ModeGrantModel } from "../models/AccountPermissionModel";
import AccountBadgeCard from "../components/AccountBadgeCard/AccountBadgeCard";
import { PresetGroups } from "../constants/PresetConfig";

interface IProps {
  blockKitStore?: BlockKitStore;
}

const AccountPermissionPage: React.FC<IProps> = inject("blockKitStore")(observer(({ blockKitStore }) => {
  const controller = blockKitStore.studioController;

  const [accounts, setAccounts] = useState<AdminUserModel[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);
  const [edited, setEdited] = useState<AdminUserModel>(() => blockKitStore.createAccount());
  const [original, setOriginal] = useState<AdminUserModel>(() => blockKitStore.createAccount());
  const [isShowCreate, setIsShowCreate] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true);
  const [isLoadingGrants, setIsLoadingGrants] = useState(false);

  // 권한 부여 후보 = 스테이징 모드 카탈로그 (ModeJsonModel 그대로 사용)
  const modes = blockKitStore.allModes;
  // 부여된 카테고리/라이선스 칩 이름 해석용 — 전체 카탈로그 (JsonModel 그대로 사용)
  const categories = blockKitStore.allCategories;
  const licenses = useMemo(() => blockKitStore.allLicenses.filter((license) => license.id), [blockKitStore.allLicenses]);

  // 최초 로드 — 계정 목록(users)만 먼저 띄우고(≈0.4s), 후보 카탈로그는 백그라운드로(staging이 ≈3.5s라 목록을 막지 않음)
  useEffect(() => {
    (async () => {
      try {
        await blockKitStore.loadAccountUsersAsync();
        setAccounts(blockKitStore.allUsers);
      } finally {
        setIsLoadingAccounts(false);
      }
      blockKitStore.loadAccountCatalogAsync(); // 우측 패널(모드/카테고리/라이선스) 후보용 — 비동기 백그라운드
    })();
  }, []);

  // 네비바 높이가 가변(expand="xxl" 줄바꿈)이라 하드코딩 대신 실제 상단 위치를 측정해
  // 뷰포트 바닥까지 정확히 채운다 → rail 하단 버튼이 잘리지 않도록.
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxHeight, setMaxHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setMaxHeight(window.innerHeight - el.getBoundingClientRect().top);
    update();
    const resizeObserver = new ResizeObserver(update);
    resizeObserver.observe(document.body);
    window.addEventListener("resize", update);
    return () => { resizeObserver.disconnect(); window.removeEventListener("resize", update); };
  }, []);

  const isDirty = useMemo(() => !!selectedUserId && !isEqual(edited, original), [selectedUserId, edited, original]);

  // 해당 유저의 부여 현황(로케일 + 모드별 카테고리/라이선스)을 서버에서 로드
  const loadAccountWithGrants = async (account: AdminUserModel): Promise<AdminUserModel> => {
    const [modeIds, allowedLocales] = await Promise.all([
      controller.getAdminUserModesAsync(account.userId),
      controller.getUserLocalesAsync(account.userId),
    ]);
    const grants: ModeGrantModel[] = await Promise.all(
      modeIds.map(async (modeId) => {
        const [categories, licenses] = await Promise.all([
          controller.getUserModeCategoriesAsync(account.userId, modeId),
          controller.getUserModeLicensesAsync(account.userId, modeId),
        ]);
        return { modeId, isIncludeCategories: false, categories, licenses };
      })
    );
    return { ...account, allowedLocales, modes: grants };
  };

  // 로드한 계정을 edited/original에 반영 (name·modes 정규화)
  const applyAccount = (loadedAccount: AdminUserModel) => {
    const normalized = { ...loadedAccount, name: loadedAccount.name ?? "", modes: loadedAccount.modes ?? [] };
    setEdited(blockKitStore.copyAccount(normalized));
    setOriginal(blockKitStore.copyAccount(normalized));
  };

  // 계정 선택 — 부여 현황 로드
  const selectAccount = async (account: AdminUserModel) => {
    if (isDirty && !confirm("저장하지 않은 변경사항이 있습니다. 이동할까요?")) return;
    setSelectedUserId(account.userId);
    applyAccount(account); // 이름·역할·국가는 목록 데이터로 즉시 표시 (모드/권한은 아래서 로딩 후 채움)
    setIsLoadingGrants(true);
    try {
      applyAccount(await loadAccountWithGrants(account));
    } catch (e) {
      console.error(e);
      alert("계정 정보를 불러오지 못했습니다.");
    } finally {
      setIsLoadingGrants(false);
    }
  };

  // 새 계정 등록 모달 열기 — 선택을 해제(우측 패널을 안내문으로)해 배경에 이전 유저 정보가 남지 않게 한 뒤 모달을 띄운다.
  const openCreate = () => {
    if (isDirty && !confirm("저장하지 않은 변경사항이 있습니다. 새 계정을 만들까요?")) return;
    setSelectedUserId(null);
    setIsShowCreate(true);
  };

  // 새 계정 등록 — 모달 입력(email·password·displayName)으로 유저만 생성. 권한은 생성 후 선택해 부여.
  const createNewAccount = async (name: string, email: string, password: string) => {
    setIsCreating(true);
    try {
      await controller.postUserAsync(email, password, name); // email, password, displayName
      await blockKitStore.loadAccountUsersAsync();
      setAccounts(blockKitStore.allUsers);
      setIsShowCreate(false);
      alert("계정 생성 완료");
    } catch (e) {
      console.error(e);
      alert("계정 생성 중 오류가 발생했습니다.");
    } finally {
      setIsCreating(false);
    }
  };

  const isDifferent = (arr1: string[], arr2: string[]) => {
  return arr1.length !== arr2.length || arr1.some((v, i) => v !== arr2[i]);
  }

  // 기존 계정 저장 — original ↔ edited 차이만 API 호출
  const saveExisting = async () => {
    const userId = edited.userId;

    // 0) 이름·역할
    if (edited.name !== original.name ||
        edited.role !== original.role ||
        isDifferent(edited.allowedPresets, original?.allowedPresets)) {
      await controller.putUserAsync(userId, edited.name ?? "", edited.role, edited.allowedPresets);
    }

    // 1) 로케일
    const localeAdd = edited.allowedLocales.filter((locale) => !original.allowedLocales.includes(locale));
    const localeDel = original.allowedLocales.filter((locale) => !edited.allowedLocales.includes(locale));
    if (localeAdd.length) await controller.postUserLocalesAsync(userId, localeAdd);
    if (localeDel.length) await controller.deleteUserLocalesAsync(userId, localeDel);

    // 2) 모드 추가/삭제
    const editedModes = edited.modes ?? [];
    const originalModes = original.modes ?? [];
    const originalModeIds = originalModes.map((grant) => grant.modeId);
    const editedModeIds = editedModes.map((grant) => grant.modeId);
    const modeAdd = editedModes.filter((grant) => !originalModeIds.includes(grant.modeId));
    const modeDel = originalModes.filter((grant) => !editedModeIds.includes(grant.modeId));
    for (const grant of modeAdd) await controller.postUserModesAsync(userId, [grant.modeId], grant.isIncludeCategories);
    if (modeDel.length) await controller.deleteUserModesAsync(userId, modeDel.map((grant) => grant.modeId), true);

    // 3) 유지되는 모드의 카테고리/라이선스 diff
    for (const editedMode of editedModes.filter((grant) => originalModeIds.includes(grant.modeId))) {
      const originalMode = originalModes.find((grant) => grant.modeId === editedMode.modeId);
      if (!originalMode) continue;
      const catAdd = editedMode.categories.filter((categoryId) => !originalMode.categories.includes(categoryId));
      const catDel = originalMode.categories.filter((categoryId) => !editedMode.categories.includes(categoryId));
      if (catAdd.length) await controller.postUserModeCategoriesAsync(userId, editedMode.modeId, catAdd);
      if (catDel.length) await controller.deleteUserModeCategoriesAsync(userId, editedMode.modeId, catDel);
      const licAdd = editedMode.licenses.filter((licenseId) => !originalMode.licenses.includes(licenseId));
      const licDel = originalMode.licenses.filter((licenseId) => !editedMode.licenses.includes(licenseId));
      if (licAdd.length) await controller.postUserModeLicensesAsync(userId, editedMode.modeId, licAdd);
      if (licDel.length) await controller.deleteUserModeLicensesAsync(userId, editedMode.modeId, licDel);
    }

    // 4) 새로 추가된 모드의 개별 카테고리(미포함 추가 시)/라이선스
    for (const grant of modeAdd) {
      if (grant.categories.length && !grant.isIncludeCategories) await controller.postUserModeCategoriesAsync(userId, grant.modeId, grant.categories);
      if (grant.licenses.length) await controller.postUserModeLicensesAsync(userId, grant.modeId, grant.licenses);
    }
  };

  const save = async () => {
    setIsSaving(true);
    try {
      await saveExisting();
      applyAccount(await loadAccountWithGrants(edited)); // 서버 반영분 재조회로 동기화
      alert("저장 완료");
    } catch (e) {
      console.error(e);
      alert("저장 중 오류가 발생했습니다.");
      // 일부만 반영됐을 수 있으니 서버 상태로 재동기화 (실패해도 무시)
      try { applyAccount(await loadAccountWithGrants(edited)); } catch { /* noop */ }
    } finally {
      setIsSaving(false);
    }
  };

  const revert = () => setEdited(blockKitStore.copyAccount(original));

  // 카드 번호 동적 부여
  let cardNum = 0;
  const nextCardNum = () => (cardNum += 1);

  return (
    <Container ref={containerRef} fluid className="d-flex flex-column" style={{ height: maxHeight ?? "calc(100vh - 60px)", overflow: "hidden" }}>
      <h4 className="m-2 pt-3 fw-bold flex-shrink-0">계정 권한 설정</h4>

      {/* gx-3: 가로 거터만. g-3의 세로 거터(margin-top:-1rem)는 고정높이에서 하단 1rem 넘침 → rail 버튼 잘림 */}
      <Row className="gx-3 flex-grow-1" style={{ minHeight: 0 }}>
        {/* 좌측 계정 목록 — 고정(자체 목록만 내부 스크롤) */}
        <Col xl={3} lg={4} className="h-100">
          <AccountListBox accounts={accounts} selectedUserId={selectedUserId} selectedModeCount={(edited.modes ?? []).length} onSelect={selectAccount} onNew={openCreate} isLoading={isLoadingAccounts} />
        </Col>

        {/* 우측 에디터 — 이 컬럼만 스크롤 */}
        <Col xl={9} lg={8} className="h-100" style={{ overflowY: "auto", paddingBottom: 16 }}>
          {!selectedUserId ? (
            <div className="text-muted text-center py-5">왼쪽에서 계정을 선택하세요.</div>
          ) : (
            <>
              <div className="d-flex align-items-center gap-2 mb-3">
                <Avatar name={edited.name || "?"} size={36} />
                <div>
                  <div className="fw-bold">{edited.name || "이름 없음"}</div>
                  <div className="text-muted small">{edited.email}</div>
                </div>
              </div>

              <AccountInfoCard num={nextCardNum()} edited={edited} setEdited={setEdited} />
              <AccountLocaleCard num={nextCardNum()} edited={edited} setEdited={setEdited} />
              <AccountDataAccessCard
                num={nextCardNum()}
                edited={edited}
                setEdited={setEdited}
                modes={modes}
                categories={categories}
                deletedCategories={blockKitStore?.deletedCategories ?? []}
                licenses={licenses}
                isAccentCards
                isLoading={isLoadingGrants} />
                <AccountBadgeCard
                  num={nextCardNum()}
                  title={"프리셋"}
                  allItems={Object.values(PresetGroups)}
                  selectedItems={edited.allowedPresets}
                  setItems={(items: string[]) => setEdited({...edited, allowedPresets: items})}
                />
            </>
          )}
        </Col>
      </Row>

      {/* 저장 바 — 변경사항이 있을 때만 노출. 레이아웃 안의 하단 행이라 콘텐츠를 가리지 않음 */}
      {isDirty && (
        <div
          className="flex-shrink-0 bg-white border-top shadow-sm d-flex align-items-center gap-2 px-4 py-2"
          style={{ margin: "0 calc(var(--bs-gutter-x) * -.5)" }}
        >
          <span className="d-inline-flex align-items-center gap-2 text-muted small">
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#ffc400" }} />
            저장하지 않은 변경사항이 있습니다
          </span>
          <span className="flex-grow-1" />
          <Stack direction="horizontal" gap={1}>
            <Button variant="secondary" onClick={revert} disabled={isSaving}>되돌리기</Button>
            <Button variant="primary" onClick={save} disabled={isSaving}>
              {isSaving ? "저장 중…" : "저장"}
            </Button>
          </Stack>
        </div>
      )}

      <AccountCreateModal
        isShow={isShowCreate}
        isSaving={isCreating}
        onCreate={createNewAccount}
        onClose={() => setIsShowCreate(false)}
      />
    </Container>
  );
}));

export default AccountPermissionPage;
